!(function (root, factory) {
  if (typeof exports === 'object' && typeof module !== 'undefined') {
    module.exports = factory();
  } else if (typeof define === 'function' && define.amd) {
    define(factory);
  } else {
    (typeof globalThis !== 'undefined' ? globalThis : root || self).EmblaCarouselSwipe = factory();
  }
})(this, function () {
  'use strict';

  function isNumber(value) {
    return typeof value === 'number' && !Number.isNaN(value);
  }

  function EmblaCarouselSwipe(options = {}) {
    let emblaApi;
    let selectedIndex = 0;
    let previousIndex = 0;
    let isRtl = false;
    let duration = 700;
    let easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
    let cleanupTimer = 0;
    let reduceMotion = false;

    function clearCleanupTimer() {
      window.clearTimeout(cleanupTimer);
      cleanupTimer = 0;
    }

    function disableEmblaTranslate() {
      const { translate, slideLooper } = emblaApi.internalEngine();

      translate.clear();
      translate.toggleActive(false);

      slideLooper.loopPoints.forEach(({ translate: loopTranslate }) => {
        loopTranslate.clear();
        loopTranslate.toggleActive(false);
      });
    }

    function setHiddenStyle(slide) {
      const { style } = slide;

      style.transition = 'none';
      style.opacity = '0';
      style.zIndex = '0';
      style.pointerEvents = 'none';
      style.clipPath = isRtl
        ? 'polygon(100% 0, 100% 0, 100% 100%, 100% 100%)'
        : 'polygon(0 0, 0 0, 0 100%, 0 100%)';
    }

    function setSlidePosition(snapIndex, isVisible) {
      const { slideRegistry, scrollSnaps, containerRect, axis } = emblaApi.internalEngine();
      const snapSlides = slideRegistry[snapIndex] || [];
      const target = isVisible ? scrollSnaps[snapIndex] : containerRect.width + 2;
      const transform = `translate${axis.scroll.toUpperCase()}(${axis.direction(target)}px)`;

      for (const slideNodeIndex of snapSlides) {
        const slideNode = emblaApi.slideNodes()[slideNodeIndex];
        if (!slideNode) {
          continue;
        }

        slideNode.style.transform = transform;
      }
    }

    function setOutgoingStyle(slide) {
      const { style } = slide;

      style.transition = 'none';
      style.opacity = '1';
      style.zIndex = '1';
      style.pointerEvents = 'none';
      style.clipPath = 'polygon(0 0, 100% 0, 100% 100%, 0 100%)';
    }

    function setSelectedStyle(slide) {
      const { style } = slide;

      style.transition = 'none';
      style.opacity = '1';
      style.zIndex = '2';
      style.pointerEvents = 'auto';
      style.clipPath = 'polygon(0 0, 100% 0, 100% 100%, 0 100%)';
      style.willChange = '';
    }

    function restoreSlideStyle(slide) {
      const { style } = slide;

      style.opacity = '';
      style.zIndex = '';
      style.pointerEvents = '';
      style.clipPath = '';
      style.transition = '';
      style.willChange = '';

      if (!slide.getAttribute('style')) {
        slide.removeAttribute('style');
      }
    }

    function applyState(nextIndex, prevIndex, immediate = false) {
      const slides = emblaApi.slideNodes();
      const nextSlide = slides[nextIndex];
      const prevSlide = slides[prevIndex];
      const shouldAnimate = !immediate && !reduceMotion && nextIndex !== prevIndex;

      for (const [index, slide] of slides.entries()) {
        if (index !== nextIndex && index !== prevIndex) {
          setHiddenStyle(slide);
          setSlidePosition(index, false);
        }
      }

      if (!nextSlide) {
        return;
      }

      if (!shouldAnimate) {
        setSelectedStyle(nextSlide);
        setSlidePosition(nextIndex, true);

        if (prevSlide && prevIndex !== nextIndex) {
          setHiddenStyle(prevSlide);
          setSlidePosition(prevIndex, false);
        }
        return;
      }

      if (prevSlide) {
        setOutgoingStyle(prevSlide);
        setSlidePosition(prevIndex, true);
      }

      setHiddenStyle(nextSlide);
      setSlidePosition(nextIndex, true);
      nextSlide.style.zIndex = '2';
      nextSlide.style.opacity = '1';
      nextSlide.style.pointerEvents = 'auto';
      nextSlide.style.willChange = 'clip-path';

      requestAnimationFrame(() => {
        nextSlide.style.transition = `clip-path ${duration}ms ${easing}`;
        nextSlide.style.clipPath = 'polygon(0 0, 100% 0, 100% 100%, 0 100%)';
      });

      clearCleanupTimer();
      cleanupTimer = window.setTimeout(() => {
        if (emblaApi.selectedScrollSnap() !== nextIndex) {
          return;
        }

        if (prevSlide) {
          setHiddenStyle(prevSlide);
          setSlidePosition(prevIndex, false);
        }

        nextSlide.style.transition = 'none';
        nextSlide.style.willChange = '';
      }, duration);
    }

    function onSelect() {
      selectedIndex = emblaApi.selectedScrollSnap();
      previousIndex = emblaApi.previousScrollSnap();
      applyState(selectedIndex, previousIndex, false);
    }

    function onReInit() {
      selectedIndex = emblaApi.selectedScrollSnap();
      previousIndex = selectedIndex;
      applyState(selectedIndex, previousIndex, true);
    }

    return {
      name: 'swipe',
      options,
      init(embla) {
        emblaApi = embla;
        duration = isNumber(options.duration) ? Math.max(0, options.duration) : 700;
        easing = typeof options.easing === 'string' ? options.easing : 'cubic-bezier(0.22, 1, 0.36, 1)';
        reduceMotion = Boolean(
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
        );
        isRtl = emblaApi.internalEngine().axis.direction(1) < 0;

        disableEmblaTranslate();
        emblaApi.on('select', onSelect).on('reInit', onReInit);
        onReInit();
      },
      destroy() {
        clearCleanupTimer();
        emblaApi.off('select', onSelect).off('reInit', onReInit);
        emblaApi.slideNodes().forEach(restoreSlideStyle);
      }
    };
  }

  EmblaCarouselSwipe.globalOptions = undefined;

  return EmblaCarouselSwipe;
});
